import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { ipGet, ipPost, ipError } from "../api";
import { PlannerError, PlannerPermissionsStatus, usePlannerPermissions } from "../components/PlannerState";
import { Card, PageHeader, Badge } from "../../components/dc";

const TYPES = [
  { key: "install_forecast", label: "Install forecast", lines: true, needsLine: true },
  { key: "usage", label: "Usage (part per model)", needsLine: true },
  { key: "part_actuals", label: "Part actuals (part, DC, weeks) — for accuracy", needsLine: true },
  { key: "install_actuals", label: "Install actuals (region, model, weeks)", needsLine: true },
  { key: "catch_all", label: "Catch-all values (part, site, value)", needsLine: false },
  { key: "pick_floor", label: "Pick floor (part, DC, value)", needsLine: false },
];

function UploadCard({ t, onUploaded, canEdit }) {
  const [line, setLine] = useState("heat_pump");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const upload = async () => {
    if (!file) { toast.error("Choose a file"); return; }
    setBusy(true);
    setError(""); setResult(null);
    try {
      const fd = new FormData();
      fd.append("batch_type", t.key);
      fd.append("product_line", line);
      fd.append("file", file);
      const res = await ipPost("/inputs/upload", fd);
      setResult(res);
      toast.success(`Uploaded ${res.row_count} rows`);
      onUploaded && onUploaded();
    } catch (err) { setError(ipError(err, "Upload failed. Your previous input has not been replaced.")); }
    finally { setBusy(false); }
  };

  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 700, color: "var(--color-text-1)", marginBottom: 8 }}>{t.label}</div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
        {t.needsLine && (
          <select data-testid={`ip-upload-${t.key}-line`} aria-label={`${t.label} product line`} disabled={busy || !canEdit} className="form-input" style={{ maxWidth: 160 }} value={line} onChange={(e) => { setLine(e.target.value); setResult(null); }}>
            <option value="heat_pump">Heat pump</option>
            <option value="solar">Solar</option>
          </select>
        )}
        <input data-testid={`ip-upload-${t.key}-file`} aria-label={t.label} style={{ maxWidth: "100%", minWidth: 0 }} disabled={busy || !canEdit} type="file" accept=".csv,.xlsx,.xls" onChange={e => { setFile(e.target.files?.[0] || null); setResult(null); setError(""); }} />
        <button data-testid={`ip-upload-${t.key}-submit`} className="btn btn-primary" disabled={busy || !file || !canEdit} onClick={upload}>{busy ? "Uploading…" : "Upload"}</button>
      </div>
      <PlannerError message={error} testId={`ip-upload-${t.key}-error`} />
      {result && <p data-testid={`ip-upload-${t.key}-result`}>{result.row_count} rows saved.</p>}
      {result?.warnings?.length > 0 && (
        <div style={{ marginTop: 10 }}>
          {result.warnings.map((w, i) => (
            <div data-testid={`ip-upload-${t.key}-warning-${i}`} key={i} style={{ fontSize: 13, color: w.level === "error" ? "#fb7185" : "#fbbf24" }}>• {w.message}</div>
          ))}
        </div>
      )}
    </Card>
  );
}

function FsoCard({ canEdit }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [error, setError] = useState("");
  const analyse = async () => {
    if (!file) { toast.error("Choose the order backend file"); return; }
    setBusy(true);
    setError(""); setRes(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await ipPost("/fso/analyse", fd);
      setRes(r);
      toast.success(`Parsed ${r.orders_parsed} orders`);
    } catch (err) { setError(ipError(err)); }
    finally { setBusy(false); }
  };
  return (
    <Card style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>FSO catch-all learning</div>
      <div style={{ fontSize: 13, color: "var(--color-text-3)", marginBottom: 8 }}>
        Upload the Supply Scanner order backend (clean_order_data). We derive the ad-hoc demand each product line is actually running at, so the catch-all % can be grounded in data instead of a flat assumption.
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <input data-testid="ip-fso-file" aria-label="FSO orders file" style={{ maxWidth: "100%", minWidth: 0 }} disabled={busy || !canEdit} type="file" accept=".csv,.xlsx,.xls" onChange={(e) => { setFile(e.target.files?.[0] || null); setRes(null); setError(""); }} />
        <button data-testid="ip-fso-analyse" className="btn btn-primary" disabled={busy || !file || !canEdit} onClick={analyse}>{busy ? "Analysing…" : "Analyse"}</button>
      </div>
      <PlannerError message={error} testId="ip-fso-error" />
      {res && (
        <div style={{ marginTop: 10, overflowX: "auto" }}>
          <p data-testid="ip-fso-note">{res.note}</p>
          <table data-testid="ip-fso-results" className="data-table" style={{ width: "100%", fontSize: 13 }}>
            <thead><tr><th>Line</th><th style={{ textAlign: "right" }}>FSO qty</th><th style={{ textAlign: "right" }}>Model-driven</th><th style={{ textAlign: "right" }}>Implied uplift</th></tr></thead>
            <tbody>{Object.entries(res.implied_uplift || {}).map(([pl, u]) => (
              <tr key={pl}><td>{pl}</td><td style={{ textAlign: "right" }}>{u.fso_qty}</td><td style={{ textAlign: "right" }}>{u.model_driven_qty}</td>
                <td style={{ textAlign: "right" }}>{u.implied_uplift_pct == null ? "—" : `${u.implied_uplift_pct}%`}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

export default function Inputs() {
  const [batches, setBatches] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const permissions = usePlannerPermissions();
  const load = useCallback(() => { setError(""); setLoading(true); ipGet("/inputs/batches").then((d) => setBatches(d?.batches || [])).catch(e => setError(ipError(e))).finally(() => setLoading(false)); }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="ip-inputs-page" style={{ minWidth: 0 }}>
      <PageHeader title="Inputs" subtitle="Upload the install forecast, usage table and assumption files. Each upload is kept as an immutable batch; the latest active batch of each type feeds the forecast." />
      <PlannerError message={error} testId="ip-inputs-error" retry={load} />
      <PlannerPermissionsStatus permissions={permissions} />
      <p data-testid="ip-upload-limits">CSV or XLSX · 10MB maximum · Invalid uploads leave the current input unchanged.</p>
      {TYPES.map((t) => <UploadCard key={t.key} t={t} onUploaded={load} canEdit={permissions.can_edit} />)}
      <FsoCard canEdit={permissions.can_edit} />
      <Card>
        <div style={{ fontWeight: 700, marginBottom: 10 }}>Batch history</div>
        <div style={{ overflowX: "auto" }}>
          <table data-testid="ip-batch-history" className="data-table" style={{ width: "100%" }}>
            <thead><tr><th>Type</th><th>Line</th><th>File</th><th>Rows</th><th>Status</th><th>Uploaded</th></tr></thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id}>
                  <td>{b.batch_type}</td>
                  <td>{b.product_line}</td>
                  <td style={{ overflowWrap: "anywhere", maxWidth: 250 }}>{b.filename}</td>
                  <td style={{ textAlign: "right" }}>{b.row_count}</td>
                  <td><Badge tone={b.status === "active" ? "green" : "muted"}>{b.status}</Badge></td>
                  <td>{b.uploaded_at ? new Date(b.uploaded_at).toLocaleString("en-GB") : ""}</td>
                </tr>
              ))}
              {(loading || error || batches.length === 0) && <tr><td data-testid="ip-batches-state" colSpan={6} style={{ color: "var(--color-text-3)" }}>{loading ? "Loading uploads…" : error ? "Upload history could not be loaded." : "No uploads yet."}</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
